import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { SUPABASE_ANON_KEY, SUPABASE_URL } from "./config";
import { createClient } from "@supabase/supabase-js";
import { differenceInDays, differenceInHours, differenceInMinutes, differenceInMonths, differenceInSeconds, differenceInWeeks, formatDate, isPast } from "date-fns";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}


export const trackEvent = async (event: string, properties: Record<string, any> = {}) => {
  try {

    const payload = {
      event,
      api_key: import.meta.env.VITE_POSTHOG_KEY,
      properties: {
        ...properties,
      },
    };

    fetch(`https://us.i.posthog.com/i/v0/e`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => { });
  } catch { }
}

export const getAgeBand = (birthYear: number) => {
  const currentYear = new Date().getFullYear();
  const age = currentYear - birthYear;
  if (age < 13) return "13";
  if (age < 14) return "14";
  if (age < 15) return "15";
  return "16_plus";
}

export const checkSignupCompletion = async (accessToken: string) => {
  try {
    const client = createClient(SUPABASE_URL!, SUPABASE_ANON_KEY!, {
      global: {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        }
      }
    });
    const { data, error } = await client.rpc('has_completed_signup');

    if (error) {
      console.error('Failed to check signup completion:', error);
      return false;
    }

    //const data = await response.json();
    console.log('Signup completion check response:', data);

    // Check if used_trial is false, which means user hasn't completed signup
    return data;
  } catch (error) {
    console.error("Error checking signup completion:", error);
    return false;
  }
}

export const checkTrialClaimed = async (accessToken: string) => {
  try {
    const client = createClient(SUPABASE_URL!, SUPABASE_ANON_KEY!, {
      global: {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        }
      }
    });
    const { data, error } = await client.rpc('has_user_signed_up');
    return data;
  } catch (error) {
    console.error("Error checking trial claimed:", error);
    return false;
  }
}

export const setAgeBand = async (accessToken: string, ageBand: string) => {
  try {
    const client = createClient(SUPABASE_URL!, SUPABASE_ANON_KEY!, {
      global: {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        }
      }
    });
    const { data, error } = await client.rpc('set_age_band', { "new_age": ageBand });
    return data;
  } catch (error) {
    console.error("Error setting age band:", error);
    return false;
  }
}

export const formatTime = (date: Date, format: string) => {
  try {
    return formatDate(date, format);
  } catch (e) {
    return null;
  }
}

export const formatRelativeTime = (dueDate: string | Date) => {
  const now = new Date();
  const due = new Date(dueDate);

  // If the date is in the past, add "ago" suffix
  const isOverdue = isPast(due);

  const seconds = Math.abs(differenceInSeconds(due, now));
  const minutes = Math.abs(differenceInMinutes(due, now));
  const hours = Math.abs(differenceInHours(due, now));
  const days = Math.abs(differenceInDays(due, now));
  const weeks = Math.abs(differenceInWeeks(due, now));
  const months = Math.abs(differenceInMonths(due, now));

  let timeText = '';

  if (months > 0) {
      timeText = `${months}mo`;
  } else if (weeks > 0) {
      timeText = `${weeks}w`;
  } else if (days > 0) {
      timeText = `${days}d`;
  } else if (hours > 0) {
      timeText = `${hours}h`;
  } else if (minutes > 0) {
      timeText = `${minutes}m`;
  } else {
      timeText = `${seconds}s`;
  }

  return isOverdue ? `${timeText} ago` : timeText;
};


// compact time formatter that minimizes character count
export const formatTimeCompact = (date: Date | string) => {
  try {
    const d = typeof date === 'string' ? new Date(date) : date;
    const minutes = d.getMinutes();
    const format = minutes === 0 ? "haaa" : "h:mmaaa";
    return formatDate(d, format);
  } catch (e) {
    return null;
  }
}

export const formatDateWithRegion = (date: Date | string, options?: {
  date_format?: string;
  clock_format?: string;
  date_text_type?: string;
  relative?: boolean;
  todo_24hr?: boolean;
}) => {
  const d = typeof date === 'string' ? new Date(date) : date;

  if (!d || isNaN(d.getTime())) return '-';

  if (options?.relative) {
    return formatRelativeTime(d);
  }

  const dateFormat = options?.date_format || 'mm/dd';
  const clockFormat = options?.clock_format || (options?.todo_24hr ? '24' : '12');
  const dateTextType = options?.date_text_type || 'numeric';

  let datePart = '';

  if (dateTextType === 'text') {
    if (dateFormat === 'dd/mm') {
      datePart = formatDate(d, "d MMM");
    } else if (dateFormat === 'yyyy/mm/dd') {
      datePart = formatDate(d, "yyyy MMM d");
    } else {
      datePart = formatDate(d, "MMM d");
    }
  } else {
    if (dateFormat === 'dd/mm') {
      datePart = formatDate(d, "dd/MM");
    } else if (dateFormat === 'yyyy/mm/dd') {
      datePart = formatDate(d, "yyyy/MM/dd");
    } else {
      datePart = formatDate(d, "MM/dd");
    }
  }

  const timePart = clockFormat === '24'
    ? formatDate(d, "HH:mm")
    : formatDate(d, "h:mmaaa");

  return `${datePart} at ${timePart}`;
}